import { useState, useRef, useCallback, useEffect } from 'react';

const REFETCH_LIMIT_DEBOUNCE = 200;
const LOAD_DISTANCE_SCROLL = 300;

type FetchResponse = {
  totalFetched: number;
  totalFetchAvailable: number;
};

type UseInfiniteScrollProps = {
  ref: React.MutableRefObject<HTMLTableElement | undefined>;
  fetchData?: () => FetchResponse | Promise<FetchResponse>;
  enableInfiniteScroll?: boolean;
};

type UseInfiniteScroll = {
  isFetching: boolean;
};

const debounce = (func: () => void, timeout: number) => {
  let timer: NodeJS.Timeout;
  return (...args: unknown[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};

const useInfiniteScroll = ({ ref, fetchData, enableInfiniteScroll }: UseInfiniteScrollProps): UseInfiniteScroll => {
  const [isFetching, setIsFetching] = useState(false);
  const [infiniteScroll, setInfiniteScroll] = useState<{ totalFetched?: number; totalFetchAvailable?: number }>({
    totalFetched: undefined,
    totalFetchAvailable: undefined
  });
  const eventListener = useRef<() => void>();
  const [isMountFetch, setIsMountFetch] = useState(true);

  const fetchNextPage = useCallback(async () => {
    if (!fetchData) {
      return;
    }

    setIsFetching(true);
    const response = await fetchData();
    setIsFetching(false);
    setInfiniteScroll({
      totalFetched: response.totalFetched,
      totalFetchAvailable: response.totalFetchAvailable
    });
  }, [fetchData]);

  const fetchMoreOnBottomReached = useCallback(
    (
      containerRefElement: HTMLTableElement | undefined,
      isLoading: boolean,
      totalFetched?: number,
      totalFetchAvailable?: number
    ) => {
      if (!containerRefElement || isLoading) {
        return;
      }

      if (totalFetched === undefined || totalFetchAvailable === undefined) {
        fetchNextPage();
        return;
      }

      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (scrollHeight - scrollTop - clientHeight < LOAD_DISTANCE_SCROLL && totalFetched < totalFetchAvailable) {
        fetchNextPage();
        return;
      }

      setIsMountFetch(false);
    },
    [fetchNextPage]
  );

  useEffect(() => {
    if (!isMountFetch || !enableInfiniteScroll) {
      return;
    }

    fetchMoreOnBottomReached(ref.current, isFetching, infiniteScroll.totalFetched, infiniteScroll.totalFetchAvailable);
  }, [ref, isMountFetch, enableInfiniteScroll, fetchMoreOnBottomReached, isFetching, infiniteScroll]);

  useEffect(() => {
    if (isMountFetch || !enableInfiniteScroll) {
      return;
    }

    if (eventListener.current !== undefined) {
      ref.current?.removeEventListener('scroll', eventListener.current);
    }

    if (isFetching) {
      return;
    }

    const eventListenerWithDebounce = debounce(() => {
      fetchMoreOnBottomReached(
        ref.current,
        isFetching,
        infiniteScroll.totalFetched,
        infiniteScroll.totalFetchAvailable
      );
    }, REFETCH_LIMIT_DEBOUNCE);

    ref.current?.addEventListener('scroll', eventListenerWithDebounce);
    eventListener.current = eventListenerWithDebounce;
  }, [ref, fetchMoreOnBottomReached, isMountFetch, enableInfiniteScroll, isFetching, infiniteScroll]);

  useEffect(() => {
    const tableRef = ref.current;

    return () => {
      if (eventListener.current !== undefined) {
        tableRef?.removeEventListener('scroll', eventListener.current);
      }
    };
  }, [ref]);

  return {
    isFetching
  };
};

export default useInfiniteScroll;
